import {
  BLACK_CLASS_VALUE_RE,
  CYRILLIC_RE,
  GENERIC_FIELD_LABELS,
  GENERIC_SLOT_NAME_RE,
  IROPZ_VALUE_HINT_RE,
  PLACEHOLDER_BRACES_RE,
  RUSSIAN_FILLER_WORDS,
  SECTION_COUNT_SUFFIX_RE,
  SectionOption,
  SURFACE_VALUE_HINT_RE,
} from '../config';

export type ChecklistFieldKind = 'predefined' | 'ai';

export type ChecklistFieldDescriptor = {
  kind: ChecklistFieldKind;
  label: string;
};

const normalizeText = (value: string): string => {
  const lowered = value.toLocaleLowerCase();
  const alnumOrSpace = lowered.replace(/[^0-9a-zа-яё]+/gi, ' ');
  return alnumOrSpace.replace(/\s+/g, ' ').trim();
};

const humanizeFieldName = (rawName: string): string => {
  const cleaned = rawName
    .trim()
    .replace(/^__ai_/i, '')
    .replace(/^field_/i, '')
    .replace(/[_-]+/g, ' ');
  return cleaned.replace(/\s+/g, ' ').trim();
};

const formatFieldLabel = (value: string): string => {
  const cleaned = value.trim().replace(/\s+/g, ' ');
  if (!cleaned) {
    return '';
  }
  if (normalizeText(cleaned) === 'иропз') {
    return 'ИРОПЗ';
  }
  return cleaned.charAt(0).toLocaleUpperCase() + cleaned.slice(1);
};

const isGenericFieldLabel = (value: string): boolean => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return true;
  }
  if (/^\d+$/.test(normalized)) {
    return true;
  }
  if (/^(?:field|slot|value|param|var|variable)\s*\d*$/.test(normalized)) {
    return true;
  }
  return GENERIC_SLOT_NAME_RE.test(value.trim());
};

const genericFieldLabel = (language: string): string => {
  return GENERIC_FIELD_LABELS[language] ?? GENERIC_FIELD_LABELS.en;
};

const normalizeOptionTextForLabel = (raw: string): string => {
  return raw
    .replace(PLACEHOLDER_BRACES_RE, ' ')
    .replace(/\(\+(?:AI|ИИ|IA|KI)\)/gi, ' ')
    .replace(/выбор\s*ии/gi, ' ')
    .replace(/choice\s*ai/gi, ' ')
    .replace(/[{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const extractContextTail = (raw: string): string => {
  const cleaned = normalizeOptionTextForLabel(raw);
  if (!cleaned) {
    return '';
  }
  const segments = cleaned
    .split(/[,;:.]/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const tail = segments[segments.length - 1] ?? cleaned;
  const words = tail.split(/\s+/);
  return words.slice(-6).join(' ');
};

const extractContextHead = (raw: string): string => {
  const cleaned = normalizeOptionTextForLabel(raw);
  if (!cleaned) {
    return '';
  }
  const segments = cleaned
    .split(/[,;:.]/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const head = segments[0] ?? cleaned;
  const words = head.split(/\s+/);
  return words.slice(0, 6).join(' ');
};

const slotValuesLookLikeSurface = (slotValues: string[]): boolean => {
  return slotValues.some((value) => SURFACE_VALUE_HINT_RE.test(value.trim()));
};

const slotValuesLookLikeBlackClass = (slotValues: string[]): boolean => {
  const normalizedValues = slotValues
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  if (normalizedValues.length === 0) {
    return false;
  }
  return normalizedValues.every((value) => BLACK_CLASS_VALUE_RE.test(value));
};

const slotValuesLookLikeIropz = (slotValues: string[]): boolean => {
  return slotValues.some((value) => IROPZ_VALUE_HINT_RE.test(value.trim()));
};

const inferRussianFieldLabel = (
  slotName: string,
  previousText: string,
  followingText: string,
  slotValues: string[],
): string | null => {
  const slotLookup = normalizeText(humanizeFieldName(slotName));
  const primaryContext =
    extractContextTail(previousText) || extractContextHead(followingText);
  const normalizedPrimaryContext = normalizeText(primaryContext);
  const normalizedHeadContext = normalizeText(
    extractContextHead(followingText),
  );
  const normalizedFollowing = normalizeText(followingText);

  if (slotLookup.includes('поверх') || slotValuesLookLikeSurface(slotValues)) {
    return 'Поверхность';
  }
  if (
    slotLookup.includes('глубин') ||
    (slotLookup.includes('глубина') && slotLookup.includes('полост'))
  ) {
    return 'Глубина полости';
  }
  if (slotLookup.includes('локализ') && slotLookup.includes('полост')) {
    return 'Локализация полости';
  }
  if (slotLookup.includes('ироп') || slotValuesLookLikeIropz(slotValues)) {
    return 'ИРОПЗ';
  }
  if (slotLookup.includes('термометр')) {
    return 'Термометрия';
  }
  if (
    slotLookup.includes('блэк') ||
    (slotLookup.includes('black') && slotLookup.includes('class')) ||
    normalizedFollowing.includes('блэка') ||
    slotValuesLookLikeBlackClass(slotValues)
  ) {
    return 'Класс по Блэку';
  }
  if (
    normalizedPrimaryContext.includes('термометр') ||
    normalizedHeadContext.includes('термометр')
  ) {
    return 'Термометрия';
  }
  if (
    (slotLookup.includes('рентген') && slotLookup.includes('характер')) ||
    slotLookup.includes('характер участка')
  ) {
    return 'Характер участка';
  }
  return null;
};

const normalizeContextWords = (raw: string): string[] => {
  const cleaned = normalizeOptionTextForLabel(raw);
  if (!cleaned) {
    return [];
  }
  return cleaned.split(/\s+/).filter((word) => word.length > 0);
};

const inferShortContextLabel = (
  previousText: string,
  followingText: string,
  language: string,
): string | null => {
  if (language !== 'ru') {
    const candidates = [
      extractContextTail(previousText),
      extractContextHead(followingText),
    ];
    for (const candidate of candidates) {
      if (candidate) {
        return formatFieldLabel(candidate);
      }
    }
    return null;
  }

  const sources: Array<[boolean, string]> = [
    [true, previousText],
    [false, followingText],
  ];
  for (const [isPrevious, source] of sources) {
    const words = normalizeContextWords(source);
    if (words.length === 0) {
      continue;
    }

    const filteredWords = words.filter(
      (word) => !RUSSIAN_FILLER_WORDS.has(normalizeText(word)),
    );
    const basis = filteredWords.length > 0 ? filteredWords : words;
    const candidateWords = isPrevious ? basis.slice(-3) : basis.slice(0, 3);
    const candidate = candidateWords.join(' ').trim();
    if (candidate && CYRILLIC_RE.test(candidate)) {
      return formatFieldLabel(candidate);
    }
  }

  return null;
};

const inferFieldLabel = (
  slotName: string,
  previousText: string,
  followingText: string,
  slotValues: string[],
  language: string,
): string => {
  if (language === 'ru') {
    const russianLabel = inferRussianFieldLabel(
      slotName,
      previousText,
      followingText,
      slotValues,
    );
    if (russianLabel) {
      return russianLabel;
    }
  }

  const humanizedName = humanizeFieldName(slotName);
  if (humanizedName && !isGenericFieldLabel(humanizedName)) {
    if (language !== 'ru' || CYRILLIC_RE.test(humanizedName)) {
      return formatFieldLabel(humanizedName);
    }
  }

  const shortContextLabel = inferShortContextLabel(
    previousText,
    followingText,
    language,
  );
  if (shortContextLabel) {
    return shortContextLabel;
  }

  return genericFieldLabel(language);
};

const deduplicateFieldDescriptors = (
  descriptors: ChecklistFieldDescriptor[],
): ChecklistFieldDescriptor[] => {
  const seen = new Set<string>();
  const result: ChecklistFieldDescriptor[] = [];

  for (const descriptor of descriptors) {
    const normalizedLabel = normalizeText(descriptor.label);
    if (!normalizedLabel || seen.has(normalizedLabel)) {
      continue;
    }
    seen.add(normalizedLabel);
    result.push(descriptor);
  }

  return result;
};

const buildOptionFieldDescriptors = (
  option: SectionOption,
  language: string,
): ChecklistFieldDescriptor[] => {
  const slotMap = option.Slots;
  const orderedNames: string[] = [];

  for (const match of option.Text.matchAll(PLACEHOLDER_BRACES_RE)) {
    const slotName = match[1]?.trim();
    if (slotName && !orderedNames.includes(slotName)) {
      orderedNames.push(slotName);
    }
  }

  for (const slotName of Object.keys(slotMap)) {
    if (!orderedNames.includes(slotName)) {
      orderedNames.push(slotName);
    }
  }

  const placeholderMatches = Array.from(
    option.Text.matchAll(PLACEHOLDER_BRACES_RE),
  );
  const slotContextByName = new Map<string, [string, string]>();

  for (const [index, match] of placeholderMatches.entries()) {
    const slotName = match[1]?.trim();
    if (!slotName || typeof match.index !== 'number') {
      continue;
    }
    const previousBoundary =
      index > 0
        ? (placeholderMatches[index - 1]?.index ?? 0) +
          (placeholderMatches[index - 1]?.[0]?.length ?? 0)
        : 0;
    const followingBoundary =
      index + 1 < placeholderMatches.length
        ? placeholderMatches[index + 1]?.index ?? option.Text.length
        : option.Text.length;

    slotContextByName.set(slotName, [
      option.Text.slice(previousBoundary, match.index),
      option.Text.slice(match.index + match[0].length, followingBoundary),
    ]);
  }

  const descriptors: ChecklistFieldDescriptor[] = [];

  for (const slotName of orderedNames) {
    const [previousText, followingText] = slotContextByName.get(slotName) ?? [
      '',
      '',
    ];
    const slotValues = slotMap[slotName]?.Values ?? [];
    const label = inferFieldLabel(
      slotName,
      previousText,
      followingText,
      slotValues,
      language,
    );
    if (!label.trim()) {
      continue;
    }
    descriptors.push({
      kind:
        slotName.trim().toLocaleLowerCase().startsWith('__ai_') ||
        slotValues.length === 0
          ? 'ai'
          : 'predefined',
      label,
    });
  }

  return descriptors;
};

export const countChecklistOptions = (
  options: Array<SectionOption> | null | undefined,
): number => {
  return (options ?? []).length;
};

export const extractChecklistFieldDescriptors = (
  options: Array<SectionOption> | null | undefined,
  language = 'en',
): ChecklistFieldDescriptor[] => {
  const descriptors: ChecklistFieldDescriptor[] = [];

  for (const option of options ?? []) {
    descriptors.push(...buildOptionFieldDescriptors(option, language));
  }

  return deduplicateFieldDescriptors(descriptors);
};

export const shouldDisplayChecklistSection = (
  optionCount: number,
  fieldCount: number,
): boolean => {
  return optionCount + fieldCount > 1;
};

export const formatChecklistSectionDisplayLabel = (
  sectionTitle: string,
  optionCount: number,
): string => {
  if (optionCount > 1) {
    return `${sectionTitle} (${optionCount})`;
  }
  return sectionTitle;
};

export const getChecklistSectionOptionCount = (
  label: string,
): number | null => {
  const match = label.match(SECTION_COUNT_SUFFIX_RE);
  if (!match?.[1]) {
    return null;
  }
  return Number.parseInt(match[1], 10);
};

export const stripSingleOptionCountFromLabel = (label: string): string => {
  if (getChecklistSectionOptionCount(label) !== 1) {
    return label;
  }
  return label.replace(SECTION_COUNT_SUFFIX_RE, '').trim();
};
